import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { TroubleshootOutlined } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";

import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useUserReducer } from "../../../redux/slice/userSlice";
import { PracticeInfo } from "../../../redux/slice/practiceSlice";
import {
  getPatientListByPracticeId,
  resetPatientListByPracticeId,
  usePatientReducer,
} from "../../../redux/slice/patient/patientSlice";
import {
  getOrderItemsByOrderId,
  getOrdersByPatientId,
  OrderInfo,
} from "../../../redux/slice/order/OrderSlice";
import {
  setSingleAlertObj,
  SingleAlertInfo,
} from "../../../redux/slice/commonSlice";
import {
  PRODUCT_CATEGORY,
  ORDER_STATUS,
  ORDERTYPE,
} from "../../../constants/applicationConstants";
import { fullName, sortNames, getCategoryDisplayLabel } from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { OrderItemRow } from "../orders/TissueTrackingForMultipleOrders";
import { ClipboardList } from "lucide-react";


interface RecordTreatmentModalProps {
  open: boolean;
  onClose: () => void;
}

interface TreatmentFormData {
  practice: string | number;
  patientId: string | number;
  orderId: string | number;
}

type DropdownOption = { value: string | number; label: string };


const RecordTreatmentModal: React.FC<RecordTreatmentModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const { practiceByUserRoleUserSlice } = useUserReducer();
  const { patientList } = usePatientReducer();
  const { allCodeTablesValuesMap } = useCodetablesSlice();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deliveredOrders, setDeliveredOrders] = useState<OrderInfo[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TreatmentFormData>({
    defaultValues: { practice: "", patientId: "", orderId: "" },
    mode: "onSubmit",
  });

  const selectedPractice = watch("practice");
  const selectedPatientId = watch("patientId");

  useEffect(() => {
    if (open) {
      dispatch(resetPatientListByPracticeId());
      setDeliveredOrders([]);
      if (practiceByUserRoleUserSlice?.length === 1) {
        const singleId = practiceByUserRoleUserSlice[0].practiceId;
        setValue("practice", singleId);
        loadPatientsForPractice(singleId.toString());
      }
    }
  }, [open]);

  useEffect(() => {
    if (selectedPractice) {
      dispatch(resetPatientListByPracticeId());
      setValue("patientId", "");
      setValue("orderId", "");
      setDeliveredOrders([]);
      loadPatientsForPractice(selectedPractice.toString());
    }
  }, [selectedPractice]);

  useEffect(() => {
    if (selectedPatientId) {
      setValue("orderId", "");
      setDeliveredOrders([]);
      loadDeliveredOrdersForPatient(Number(selectedPatientId));
    }
  }, [selectedPatientId]);


  const loadPatientsForPractice = async (practiceId: string) => {
    setLoadingPatients(true);
    await dispatch(getPatientListByPracticeId({ practiceId }));
    setLoadingPatients(false);
  };

  const loadDeliveredOrdersForPatient = async (patientId: number) => {
    setLoadingOrders(true);
    try {
      const response = await dispatch(getOrdersByPatientId(patientId));

      if (
        response.payload?.code === "V3OK" &&
        response.payload?.message === "SUCCESS" &&
        (!response.payload?.errors || response.payload.errors.length === 0)
      ) {
        const allOrders: OrderInfo[] = response.payload.data ?? [];
        const delivered = allOrders.filter(
          (o) => o.orderStatus === ORDER_STATUS.DELIVERED,
        );

        if (delivered.length === 0) {
          showAlert(
            "No delivered orders found for this patient. Record Tx only applies to delivered orders.",
            "error",
          );
          return;
        }

        const promises = delivered.map((order) =>
          dispatch(getOrderItemsByOrderId(order.orderId)).then(unwrapResult),
        );
        const results = await Promise.all(promises);
        const allItems: OrderItemRow[] = results.flatMap((r) => r.data);

        if (
          allItems.every((item) => item.productCategory === PRODUCT_CATEGORY.CONSERVATIVE)
        ) {
          showAlert(`Record Tx is not supported for ${getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.CONSERVATIVE)} orders.`, "error");
          return;
        }

        const filteredOrders = delivered.filter((order) => {
          const items = results[delivered.indexOf(order)]
            ?.data as OrderItemRow[];
          return items?.some(
            (item) => item.productCategory !== PRODUCT_CATEGORY.CONSERVATIVE,
          );
        });

        setDeliveredOrders(filteredOrders);
      } else {
        showAlert(
          "An error occurred while fetching orders for this patient.",
          "error",
        );
      }
    } catch {
      showAlert(
        "An error occurred while fetching orders. Please try again.",
        "error",
      );
    } finally {
      setLoadingOrders(false);
    }
  };

  const showAlert = (
    message: string,
    alertType: "success" | "error" | "info",
  ) => {
    const alertObj: SingleAlertInfo = { message, alertType };
    dispatch(setSingleAlertObj(alertObj));
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setDeliveredOrders([]);
      dispatch(resetPatientListByPracticeId());
      reset();
    }, 300);
  };

  const onFormSubmit = async (data: TreatmentFormData) => {
    if (!data.patientId || !data.orderId) return;
    setIsSubmitting(true);

    try {
      const selectedOrder = deliveredOrders.find(
        (o) => o.orderId === Number(data.orderId),
      );

      if (!selectedOrder) {
        showAlert("Selected order not found. Please try again.", "error");
        return;
      }

      const itemsResponse = await dispatch(
        getOrderItemsByOrderId(selectedOrder.orderId),
      ).then(unwrapResult);

      const orderItems: OrderItemRow[] = (itemsResponse.data as OrderItemRow[])
        .filter((item) => item.productCategory !== PRODUCT_CATEGORY.CONSERVATIVE)
        .map((item) => {
          const tissueTrackingInfo: Record<string, any> =
            item.tissueTrackingInfo || {};
          const woundDetails =
            tissueTrackingInfo[`woundData_${item.orderItemId}`] || {};
          return {
            ...item,
            woundLocation: woundDetails?.woundLocationLabel || null,
          };
        })
        .sort((a, b) => a.units - b.units);

      const matchedPatient = patientList?.find(
        (p: any) => p.patientId === Number(data.patientId),
      );
      const patientName = matchedPatient
        ? fullName(
            "",
            matchedPatient.firstName,
            matchedPatient.middleName,
            matchedPatient.lastName,
          )
        : "";

      if (selectedOrder.orderType === ORDERTYPE.CONSIGNMENT_ORDER) {
        navigate("/create_consignment_order", { state: { selectedOrder } });
      } else {
        navigate("/multiple_tissue_tracking", {
          state: { patientId: Number(data.patientId), patientName, orderItems },
        });
      }

      handleClose();
    } catch {
      showAlert(
        "An error occurred while loading order items. Please try again.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const practiceOptions: DropdownOption[] = useMemo(() => {
    if (!practiceByUserRoleUserSlice?.length) return [];
    const sorted = sortNames(practiceByUserRoleUserSlice, "practiceName");
    return sorted.map((p: PracticeInfo) => ({
      value: p.practiceId,
      label: p.practiceName,
    }));
  }, [practiceByUserRoleUserSlice]);

  const patientOptions: DropdownOption[] = useMemo(() => {
    if (!patientList?.length) return [];
    return patientList.map((p: any) => ({
      value: p.patientId,
      label:
        fullName("", p.firstName, p.middleName, p.lastName) +
        " — DOB: " +
        p.birthDate,
    }));
  }, [patientList]);

  const orderOptions: DropdownOption[] = useMemo(() => {
    return deliveredOrders.map((o) => ({
      value: o.orderId,
      label: `${o.orderNumber}${o.productName ? " — " + o.productName : ""}`,
    }));
  }, [deliveredOrders]);


  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const ErrorMsg = ({ msg }: { msg?: string }) =>
    msg ? (
      <Typography sx={{ color: tokens.error, fontSize: "0.75rem", mt: 0.5 }}>
        {msg}
      </Typography>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <ClipboardList />
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Record TX
          </Typography>
          <Typography
            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
          >
            Select practice, patient and order
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Box sx={bodySx}>
          <Box>
            <FieldLabel label="Practice" required />
            <Box sx={dropdownWrapSx(!!errors.practice)}>
              <CustomDropdownField
                name="practice"
                control={control}
                options={practiceOptions}
                rules={{ required: "Please select a practice to continue." }}
                size="small"
                placeholderText="Select a practice..."
                sortOptions={false}
                disabled={practiceOptions.length === 1}
              />
            </Box>
          </Box>

          <Box>
            <FieldLabel label="Patient" required />
            <Box sx={dropdownWrapSx(!!errors.patientId)}>
              <CustomDropdownField
                name="patientId"
                control={control}
                options={patientOptions}
                rules={{ required: "Please select a patient to continue." }}
                size="small"
                placeholderText={
                  !selectedPractice
                    ? "Select a practice first..."
                    : loadingPatients
                      ? "Loading patients..."
                      : patientOptions.length === 0
                        ? "No active patients found..."
                        : "Select a patient..."
                }
                sortOptions={false}
                disabled={!selectedPractice || loadingPatients}
              />
            </Box>
          </Box>

          <Box>
            <FieldLabel label="Delivered Order" required />
            <Box sx={dropdownWrapSx(!!errors.orderId)}>
              <CustomDropdownField
                name="orderId"
                control={control}
                options={orderOptions}
                rules={{ required: "Please select an order to continue." }}
                size="small"
                placeholderText={
                  !selectedPatientId
                    ? "Select a patient first..."
                    : loadingOrders
                      ? "Loading orders..."
                      : orderOptions.length === 0
                        ? "No eligible orders found..."
                        : "Select an order..."
                }
                sortOptions={false}
                disabled={
                  !selectedPatientId ||
                  loadingOrders ||
                  orderOptions.length === 0
                }
              />
            </Box>
            {selectedPatientId &&
              !loadingOrders &&
              orderOptions.length === 0 && (
                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    color: tokens.textSecondary,
                    mt: 0.5,
                  }}
                >
                  Only delivered, {getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.ADVANCED)} orders are eligible for Record Tx.
                </Typography>
              )}
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={isSubmitting || loadingOrders}
              startIcon={
                <CheckCircleOutlineIcon sx={{ fontSize: "1rem !important" }} />
              }
              sx={primaryBtnSx}
            >
              {isSubmitting ? "Processing..." : "Record Tx"}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={handleClose}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default RecordTreatmentModal;
